<template>
  <div class="search-block card">
    <el-form :model="searchBlockForm" :rules="searchBlockRules" label-position="top">

      <div class="row">
        <div class="col col-xs-12 col-md-6">
          <el-form-item label="First name" prop="firstName" :error="searchBlockErrors.firstName">
            <el-input v-model="searchBlockForm.firstName" />
          </el-form-item>
        </div>
        <div class="col col-xs-12 col-md-6">
          <el-form-item label="Last name" prop="lastName" :error="searchBlockErrors.lastName">
            <el-input v-model="searchBlockForm.lastName" />
          </el-form-item>
        </div>
      </div>
      
      <div class="row">
        <div class="col col-xs-12 col-md-6">
          <el-form-item label="Activity" prop="activityId" :error="searchBlockErrors.activityId">
            <el-select v-model="searchBlockForm.activityId" class="wide" @change="handleChangeActivity">
              <el-option label="Any" :value="''" />
              <el-option v-for="type in descriptions.activityType" :key="type.value" :label="type.description"
                :value="type.value" />
            </el-select>
          </el-form-item>
        </div>

        <div class="col col-xs-12 col-md-6">
          <el-form-item label="Skill" prop="skillTypeId" :error="searchBlockErrors.skillTypeId">
            <el-select v-model="searchBlockForm.skillTypeId" class="wide" :disabled="!isActivityChosen">
              <el-option label="Any" :value="''" />
              <el-option v-for="type in descriptions.skillLevel" :key="type.value" :label="type.description"
                :value="type.value" />
            </el-select>
          </el-form-item>
        </div>

        <div class="col col-xs-12 col-md-6">
          <el-form-item label="Minimum skill level" prop="minimalSkillLevel"
            :error="searchBlockErrors.minimalSkillLevel">
            <el-select v-model="searchBlockForm.minimalSkillLevel" class="wide"
              :disabled="!searchBlockForm.skillTypeId">
              <el-option label="Any" :value="''" />
              <el-option v-for="type in descriptions.skillLevel" :key="type.value" :label="type.description"
                :value="type.value" />
            </el-select>
          </el-form-item>
        </div>

        <div class="col col-xs-12 col-md-6">
          <el-form-item label="Certificate" prop="certificateTypeId" :error="searchBlockErrors.certificateTypeId">
            <el-select v-model="searchBlockForm.certificateTypeId" class="wide" :disabled="!isActivityChosen">
              <el-option label="Any" :value="''" />
              <el-option v-for="type in sertificateTypeDescriptions" :key="type.value" :label="type.description"
                :value="type.value" />
            </el-select>
          </el-form-item>
        </div>

        <div class="col col-xs-12">
          <p class="search-block__subtitle">
            Level
          </p>
        </div>
        <div class="col col-xs-12 col-md-6">
          <el-form-item label="Not lower" prop="levelFrom" :error="searchBlockErrors.levelFrom">
            <el-select v-model="searchBlockForm.levelFrom" class="wide">
              <el-option label="Any" :value="''" />
              <el-option v-for="type in descriptions.activityCVLevel" :key="type.value" :label="type.description"
                :value="type.value" />
            </el-select>
          </el-form-item>
        </div>

        <div class="col col-xs-12 col-md-6">
          <el-form-item label="Not higher" prop="levelTo" :error="searchBlockErrors.levelTo">
            <el-select v-model="searchBlockForm.levelTo" class="wide">
              <el-option label="Any" :value="''" />
              <el-option v-for="type in descriptions.activityCVLevel" :key="type.value" :label="type.description"
                :value="type.value" />
            </el-select>
          </el-form-item>
        </div>

        <div class="col col-xs-12">
          <el-form-item label="Roles" prop="roleIds" :error="searchBlockErrors.roleIds">
            <el-select v-model="searchBlockForm.roleIds" multiple class="wide" :disabled="!isActivityChosen">
              <el-option v-for="type in roleTypeDescriptions" :key="type.value" :label="type.description"
                :value="type.value" />
            </el-select>
          </el-form-item>
        </div>

        <div class="col col-xs-12">
          <el-form-item label="Functions" prop="achievementFunctions" :error="searchBlockErrors.achievementFunctions">
            <el-select v-model="searchBlockForm.achievementFunctions" multiple class="wide"
              :disabled="!isActivityChosen">
              <el-option v-for="type in achievementFunctonDescriptions" :key="type.value" :label="type.description"
                :value="type.value" />
            </el-select>
          </el-form-item>
        </div>

        <div class="col col-xs-12">
          <el-form-item label="Keywords" prop="keywords" :error="searchBlockErrors.keywords">
            <el-input v-model="searchBlockForm.keywords" />
          </el-form-item>
        </div>
      </div>

      <div class="search-block__buttons">
        <el-button @click="resetBlockForm" native-type="reset">
          Clear all
        </el-button>
        <el-button @click="handleRemoveBlock" type="primary" icon="el-icon-delete" v-if="isRemovable">
          Remove
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { parseErrorsByFields } from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';

import { getAchievementFunctionDescriptions } from '@/api/events.js';
import { getSkillTypeDescriptions } from '@/api/skill.js';
import { getCertificateTypeDescriptions } from '@/api/certificate.js';
import { getAchievementRoleTypeDescriptions } from '@/api/achievement.js';

export default {
  name: 'SearchBlock',
  props: {
    isRemovable: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      searchBlockForm: {
        firstName: null,
        lastName: null,
        levelFrom: null,
        levelTo: null,
        activityId: null,
        skillTypeId: null,
        minimalSkillLevel: null,
        certificateTypeId: null,
        roleIds: [],
        achievementFunctions: [],
        keywords: null,
      },
      searchBlockErrors: {
        firstName: null,
        lastName: null,
        levelFrom: null,
        levelTo: null,
        activityId: null,
        skillTypeId: null,
        minimalSkillLevel: null,
        certificateTypeId: null,
        roleIds: null,
        achievementFunctions: null,
        keywords: null,
      },
      searchBlockRules: {
        firstName: [{ required: false }],
        lastName: [{ required: false }],
        levelFrom: [{ required: false }],
        levelTo: [{ required: false }],
        activityId: [{ required: false }],
        skillTypeId: [{ required: false }],
        minimalSkillLevel: [{ required: false }],
        certificateTypeId: [{ required: false }],
        roleIds: [{ required: false }],
        achievementFunctions: [{ required: false }],
        keywords: [{ required: false }],
      },

      // if activity
      skillTypeDescriptions: [],
      sertificateTypeDescriptions: [],
      roleTypeDescriptions: [],
      achievementFunctonDescriptions: [],
    };
  },
  computed: {
    descriptions() {
      return this.$store.state.descriptions;
    },
    formData() {
      const { searchBlockForm } = this;

      return {
        level: {
          from: searchBlockForm.levelFrom,
          to: searchBlockForm.levelTo,
        },
        activity: {
          activityId: searchBlockForm.activityId,
          skill: {
            skillTypeId: searchBlockForm.skillTypeId,
            minimalSkillLevel: searchBlockForm.minimalSkillLevel,
          },
          certificateTypeId: searchBlockForm.certificateTypeId,
          roleIds: searchBlockForm.roleIds,
        },
        achievementFunctions: searchBlockForm.achievementFunctions,
        keywords: searchBlockForm.keywords,
        firstName: searchBlockForm.firstName,
        lastName: searchBlockForm.lastName,
      };
    },
    isActivityChosen() {
      return this.searchBlockForm.activityId !== null;
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    async fetchAchievementTypeDescriptions() {
      const { activityId } = this.searchBlockForm;
      const { success, data } = await getAchievementFunctionDescriptions(
        activityId,
      );

      if (success) {
        this.achievementFunctonDescriptions = data.items;
      }
    },
    async fetchSkillTypeDescriptions() {
      const { activityId } = this.searchBlockForm;
      const { data, success } = await getSkillTypeDescriptions({ activityId });
      if (success) {
        this.skillTypeDescriptions = data.items;
      }
    },
    async fetchCertificateTypeDescriptions() {
      const { activityId } = this.searchBlockForm;
      const { data, success } = await getCertificateTypeDescriptions({
        activityId,
      });
      if (success) {
        this.sertificateTypeDescriptions = data.items;
      }
    },
    async fetchRoleTypeDescriptions() {
      const { activityId } = this.searchBlockForm;
      const { data, success } = await getAchievementRoleTypeDescriptions({
        activityId,
      });
      if (success) {
        this.roleTypeDescriptions = data.items;
      }
    },
    handleChangeActivity(value) {
      this.skillTypeId = null;
      this.certificateTypeId = null;
      this.roleIds = [];
      this.achievementFunctions = [];

      this.handleSetDescriptions();
    },
    handleSetDescriptions() {
      this.fetchSkillTypeDescriptions();
      this.fetchCertificateTypeDescriptions();
      this.fetchRoleTypeDescriptions();
      this.fetchAchievementTypeDescriptions();
    },
    resetBlockForm() {
      this.searchBlockForm = {
        firstName: null,
        lastName: null,
        levelFrom: null,
        levelTo: null,
        levelSearchType: null,
        activityId: null,
        skillTypeId: null,
        minimalSkillLevel: null,
        certificateTypeId: null,
        roleIds: [],
        achievementFunctions: [],
        keywords: null,
      };
      this.clearErrors();
    },
    clearErrors() {
      this.searchBlockErrors = {
        firstName: null,
        lastName: null,
        levelFrom: null,
        levelTo: null,
        levelSearchType: null,
        activityId: null,
        skillTypeId: null,
        minimalSkillLevel: null,
        certificateTypeId: null,
        roleIds: null,
        achievementFunctions: null,
        keywords: null,
      };
    },
    getSearchBlockData() {
      const { formData } = this;
      return formData;
    },
    handleRemoveBlock() {
      this.$emit('remove-search-block');
    },
  },
};
</script>

<style lang="scss">
.search-block {
  margin-bottom: var(--global-dimension-size-400);

  &__subtitle {
    margin-top: 0;
  }

  &__buttons {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
  }
}
</style>
